import { useMemo, useState } from 'react';

import { ChevronRightIcon } from '@/components/Icons';
import { Modal } from '@/components/Modal';
import {
  PHONE_COUNTRIES,
  getPhoneCountry,
  normalizePhoneDigits,
  type PhoneCountryCode,
} from '@/lib/phone';

type PhoneNumberFieldProps = {
  label?: string;
  countryCode: PhoneCountryCode;
  phoneNumber: string;
  onChangeCountryCode: (countryCode: PhoneCountryCode) => void;
  onChangePhoneNumber: (phoneNumber: string) => void;
  placeholder?: string;
  errorText?: string;
  maxLength?: number;
  autoComplete?: string;
  disabled?: boolean;
};

export function PhoneNumberField({
  label = 'Phone number',
  countryCode,
  phoneNumber,
  onChangeCountryCode,
  onChangePhoneNumber,
  placeholder = '9876543210',
  errorText,
  maxLength,
  autoComplete = 'tel',
  disabled = false,
}: PhoneNumberFieldProps) {
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const selectedCountry = useMemo(() => getPhoneCountry(countryCode), [countryCode]);

  function handleSelectCountry(nextCountryCode: PhoneCountryCode) {
    onChangeCountryCode(nextCountryCode);
    setIsPickerOpen(false);
  }

  return (
    <div className="field phone-number-field">
      {label ? <span className="field-label">{label}</span> : null}

      <div className={`phone-number-row${disabled ? ' disabled' : ''}`}>
        <button
          className="phone-country-button"
          type="button"
          aria-label={`Country code ${selectedCountry.dialCode}`}
          aria-haspopup="dialog"
          aria-expanded={isPickerOpen}
          disabled={disabled}
          onClick={() => setIsPickerOpen(true)}>
          <span className="phone-country-flag" aria-hidden="true">
            {selectedCountry.flag}
          </span>
          <span className="phone-country-dial">{selectedCountry.dialCode}</span>
          <ChevronRightIcon size={14} className="phone-country-chevron" />
        </button>

        <input
          className="input phone-number-input"
          value={phoneNumber}
          onChange={(event) => onChangePhoneNumber(normalizePhoneDigits(event.target.value))}
          placeholder={placeholder}
          inputMode="tel"
          autoComplete={autoComplete}
          maxLength={maxLength}
          disabled={disabled}
        />
      </div>

      {errorText ? <span className="field-error">{errorText}</span> : null}

      <Modal
        open={isPickerOpen}
        title="Choose country code"
        description="Default is India +91."
        onClose={() => setIsPickerOpen(false)}>
        <div className="phone-country-list">
          {PHONE_COUNTRIES.map((country) => {
            const selected = country.code === countryCode;

            return (
              <button
                key={country.code}
                type="button"
                className={`phone-country-option${selected ? ' selected' : ''}`}
                aria-pressed={selected}
                onClick={() => handleSelectCountry(country.code)}>
                <span className="phone-country-option-copy">
                  <strong>
                    {country.flag} {country.name}
                  </strong>
                  <span>{country.dialCode}</span>
                </span>
                <span className="badge soft">{selected ? 'Selected' : 'Use'}</span>
              </button>
            );
          })}
        </div>
      </Modal>
    </div>
  );
}
